import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Trash2 } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import FormField from "../../components/forms/FormField/FormField.jsx";
import Select from "../../components/forms/Select/Select.jsx";
import TextInput from "../../components/forms/TextInput/TextInput.jsx";
import Checkbox from "../../components/forms/Checkbox/Checkbox.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import ShareRow from "../../components/share/ShareRow.jsx";
import { ROUTE_META } from "../../seo/siteMeta.js";
import { breadcrumbSchema, faqSchema } from "../../seo/schema.js";
import { RATES, WEIGHTS, MONTHS } from "../../data/rates.js";
import { vehicleTax } from "../../data/partialPeriodTax.js";
import { dueDateFor, currentPeriod, nextDueDate } from "../../data/taxPeriods.js";
import { money } from "../../utils/format.js";

/**
 * Public Form 2290 tax calculator. Prices every vehicle from the IRS figures
 * themselves: the annual table (RATES, Form 2290 p.2) for July and the
 * Partial-Period Tax Tables (partialPeriodTax.js, i2290 p.14) for later months -
 * the same figures form2290-api files with. Deadlines come from taxPeriods.js.
 * Nothing here estimates; an input it can't price throws.
 *
 * Its job is to turn "how much is my 2290?" into a started return, so every
 * result carries a sign-up / start-filing call to action.
 */

const meta = ROUTE_META["/form-2290-tax-calculator"];
const CATEGORIES = Object.keys(RATES);
const SUSPENDED = "W";

const newRow = () => ({ id: Math.random().toString(36).slice(2), cat: "V", logging: false, count: "1" });

const FAQS = [
  {
    q: "How is Form 2290 tax calculated?",
    a: "By the vehicle's taxable gross weight category and the month it was first used in the July-to-June period. A vehicle first used in July pays the annual amount for its category; one first used later pays the amount in the IRS Partial-Period Tax Table for the months left. Logging vehicles use a reduced table.",
  },
  {
    q: "What is the most Form 2290 tax on one truck?",
    a: `$${RATES.V[0].toFixed(2)} for a full period, category V (over 75,000 lb). A logging vehicle in category V pays $${RATES.V[1].toFixed(2)}.`,
  },
  {
    q: "Do suspended vehicles owe Form 2290 tax?",
    a: "No. A vehicle expected to be used 5,000 miles or less (7,500 for agricultural vehicles) is reported as category W with no tax, but it must still be listed on the return and on Schedule 1.",
  },
  {
    q: "Is this the same amount I'll pay when I file?",
    a: "The tax uses the same IRS tables the filing uses. The total on your return also reflects any credits you claim; our service fee is separate and shown on the pricing page.",
  },
];

const TaxCalculator = () => {
  // Opens on the month whose deadline is next, not on a July that has passed.
  const [month, setMonth] = useState(() => nextDueDate()?.month || MONTHS[0][0]);
  const [rows, setRows] = useState([newRow()]);

  const update = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs));

  const priced = rows.map((r) => {
    const count = Math.max(0, parseInt(r.count, 10) || 0);
    const each = r.cat === SUSPENDED ? 0 : vehicleTax(r.cat, r.logging, month);
    return { ...r, count, each, total: Math.round(each * count * 100) / 100 };
  });
  const totalTax = Math.round(priced.reduce((s, r) => s + r.total, 0) * 100) / 100;
  const taxable = priced.filter((r) => r.cat !== SUSPENDED).reduce((s, r) => s + r.count, 0);
  const suspended = priced.filter((r) => r.cat === SUSPENDED).reduce((s, r) => s + r.count, 0);
  const due = dueDateFor(month);
  const monthLabel = MONTHS.find((m) => m[0] === month)[1];

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/form-2290-tax-calculator"
        keywords={meta.keywords}
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Form 2290 tax calculator", path: "/form-2290-tax-calculator" },
          ]),
          faqSchema(FAQS),
        ]}
      />
      <PageHeader eyebrow={`Tax period ${currentPeriod().label}`} title="Form 2290 Tax Calculator">
        Work out your Heavy Vehicle Use Tax for any number of trucks in seconds. Pick the month the vehicles were
        first used on a public highway, add each weight category, and see the tax and the filing deadline —
        figured from the IRS rate tables, the same ones your return is filed with.
      </PageHeader>

      <div className="grid gap-6 mt-8 lg:grid-cols-[1.4fr_1fr] items-start">
        <Panel padded>
          <FormField label="Month first used on a public highway" hint="One Form 2290 covers the vehicles first used in one month.">
            <Select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month first used">
              {MONTHS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>

          <div className="grid gap-3">
            {priced.map((r, i) => (
              <div key={r.id} className="border border-[var(--line)] rounded-[12px] p-3.5">
                <div className="grid gap-3 sm:grid-cols-[1.6fr_.7fr_auto] items-end">
                  <FormField label={`Vehicle group ${i + 1}: weight category`} style={{ marginBottom: 0 }}>
                    <Select value={r.cat} onChange={(e) => update(r.id, { cat: e.target.value, logging: e.target.value === SUSPENDED ? false : r.logging })}>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c} — {WEIGHTS[c]}
                        </option>
                      ))}
                      <option value={SUSPENDED}>W — suspended (5,000 mi or less; 7,500 agricultural)</option>
                    </Select>
                  </FormField>
                  <FormField label="Number of trucks" style={{ marginBottom: 0 }}>
                    <TextInput
                      type="number"
                      min="0"
                      inputMode="numeric"
                      value={r.count}
                      onChange={(e) => update(r.id, { count: e.target.value })}
                    />
                  </FormField>
                  <button
                    type="button"
                    onClick={() => remove(r.id)}
                    disabled={rows.length === 1}
                    className="h-11 px-3 text-[var(--muted)] disabled:opacity-30 hover:text-[var(--danger)]"
                    aria-label="Remove this vehicle group"
                  >
                    <Trash2 size={17} />
                  </button>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 mt-2.5">
                  {r.cat === SUSPENDED ? (
                    <span className="text-sm text-[var(--muted)]">No tax — but it must still be reported.</span>
                  ) : (
                    <Checkbox
                      id={`log-${r.id}`}
                      checked={r.logging}
                      onChange={(e) => update(r.id, { logging: e.target.checked })}
                      title="Logging vehicle (reduced rate)"
                    />
                  )}
                  <span className="text-sm">
                    {money(r.each)} each · <b>{money(r.total)}</b>
                  </span>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setRows((rs) => [...rs, newRow()])}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--accent)]"
          >
            <Plus size={16} /> Add another weight category
          </button>
        </Panel>

        <Panel padded className="lg:sticky lg:top-6 !bg-[var(--accent-soft)]">
          <div className="text-xs uppercase tracking-[.1em] text-[var(--muted)] font-semibold">Your Form 2290 tax</div>
          <div className="font-disp text-[40px] leading-none mt-2">{money(totalTax)}</div>
          <div className="text-sm text-[var(--muted)] mt-2">
            {taxable} taxable {taxable === 1 ? "truck" : "trucks"}
            {suspended > 0 && ` · ${suspended} suspended`} · first used {monthLabel}
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--line)] text-sm">
            File and pay by <b>{due.dueLabel}</b>
            <div className="text-[var(--muted)] mt-1">
              {due.monthsRemaining === 12
                ? "Full-period tax (July first use)."
                : `Partial-period tax: ${due.monthsRemaining} months left in the period.`}
            </div>
          </div>
          {taxable >= 25 && (
            <div className="text-sm mt-3">
              25 or more taxable vehicles on one return must be e-filed — upload them from a spreadsheet.
            </div>
          )}
          <div className="grid gap-2.5 mt-5">
            <Link to="/login?tab=up">
              <Button block>
                Create your free account <ArrowRight size={16} />
              </Button>
            </Link>
            <Link to="/wizard/1" className="text-center text-sm font-semibold text-[var(--accent)]">
              Already have an account? File this return →
            </Link>
          </div>
          <p className="text-xs text-[var(--muted)] mt-4">
            Tax shown is the IRS Heavy Vehicle Use Tax only. Our filing fee is separate — see{" "}
            <Link to="/pricing" className="text-[var(--accent)]">pricing</Link>.
          </p>
        </Panel>
      </div>

      <section className="mt-12 max-w-[78ch]">
        <h2 className="font-disp text-[25px]">How the tax is worked out</h2>
        <p className="mt-3 leading-[1.7]">
          Two things set the tax on each vehicle: its taxable gross weight category, and the month it was first used
          on a public highway during the July 1 – June 30 period. Vehicles in service in July pay the annual amount
          shown on the <Link to="/rates" className="text-[var(--accent)]">Form 2290 rate table</Link>. Vehicles first
          used later pay the IRS partial-period amount for the months left — see{" "}
          <Link to="/blog/form-2290-new-vehicle-partial-period" className="text-[var(--accent)]">partial-period tax for trucks first used after July</Link>.
          Not sure of the category? Start with{" "}
          <Link to="/blog/form-2290-weight-categories-and-rates" className="text-[var(--accent)]">how weight categories work</Link>.
        </p>
        <p className="mt-3 leading-[1.7]">
          Every month you put new trucks on the road needs its own return. The{" "}
          <Link to="/form-2290-due-date-calculator" className="text-[var(--accent)]">Form 2290 due date calculator</Link>{" "}
          shows the deadline for each one.
        </p>
      </section>

      <section className="mt-10 max-w-[78ch]">
        <h2 className="font-disp text-[25px] mb-4">Frequently asked questions</h2>
        <div className="grid gap-3">
          {FAQS.map((f) => (
            <Panel key={f.q} padded>
              <b className="font-disp text-[16px]">{f.q}</b>
              <p className="text-[var(--muted)] text-sm mt-1.5 leading-[1.65]">{f.a}</p>
            </Panel>
          ))}
        </div>
      </section>

      <ShareRow path="/form-2290-tax-calculator/" title="Free Form 2290 tax calculator - uses the IRS's own rate tables:" />

      <p className="text-[var(--muted)] text-xs mt-11 pt-5 border-t border-[var(--line)] max-w-[70ch]">
        Figures from the IRS Form 2290 tax computation table and the Partial-Period Tax Tables in the Instructions
        for Form 2290 (Rev. July 2026). General information, not tax advice for your situation.
      </p>
    </div>
  );
};

export default TaxCalculator;
